export const HOUSE_NUMBER_MAX_LENGTH = 10
export const STREET_LINE_MAX_LENGTH = 60
export const NO_HOUSE_NUMBER_TOKENS = [
  'o.N.',
  'oN',
  'ohne Nr.',
  's/n',
  'sn',
  'snc',
] as const

export type StreetParts = {
  street: string
  houseNumber: string
}

export type AddressLocation = StreetParts & {
  zipcode: string
  city: string
  countryId?: string
}

export type StreetAndNumberMessages = {
  required: string
  houseNumberMustStartWithDigit: string
  houseNumberHasSpaces: string
  houseNumberHasInvalidCharacters: string
  houseNumberTooLong: string
  repeatedHouseNumber: string
  streetLineTooLong: string
}

export type StreetAndNumberIssue = {
  field: keyof StreetParts
  message: string
}

export const STREET_AND_NUMBER_MESSAGES: StreetAndNumberMessages = {
  required: 'This field is required',
  houseNumberMustStartWithDigit: 'Start with a digit, or enter o.N. / s/n',
  houseNumberHasSpaces: 'Write the number without spaces, e.g. 12/3 or 12bus3',
  houseNumberHasInvalidCharacters: 'Use only letters, digits and - / .',
  houseNumberTooLong: `At most ${HOUSE_NUMBER_MAX_LENGTH} characters`,
  repeatedHouseNumber: 'The street already ends with this house number',
  streetLineTooLong: `Street and house number can have at most ${STREET_LINE_MAX_LENGTH} characters together`,
}

const escapeForPattern = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const NO_HOUSE_NUMBER = NO_HOUSE_NUMBER_TOKENS.map(escapeForPattern).join('|')
const HOUSE_NUMBER_CHARACTER = String.raw`[\p{L}\p{N}\-/.]`
const DIGIT_FIRST_HOUSE_NUMBER = String.raw`\d${HOUSE_NUMBER_CHARACTER}*`
const SEPARATED_SUFFIX = '(?:[a-z]|bis|ter|quater)'

const HOUSE_NUMBER = new RegExp(
  `^(?:${NO_HOUSE_NUMBER}|${DIGIT_FIRST_HOUSE_NUMBER})$`,
  'iu',
)
const TRAILING_HOUSE_NUMBER = new RegExp(
  String.raw`^(?<street>.*\S)\s+(?<houseNumber>${NO_HOUSE_NUMBER}|${DIGIT_FIRST_HOUSE_NUMBER}(?:\s${SEPARATED_SUFFIX})?)$`,
  'iu',
)
const LEADING_HOUSE_NUMBER = new RegExp(
  String.raw`^(?<houseNumber>\d{1,5}(?:[-/]\d{1,5})?(?:\s?${SEPARATED_SUFFIX})?)(?:\s*,\s*|\s+)(?<street>\D.*)$`,
  'iu',
)
const LEADING_NUMBER_STREET_TYPES = [
  'rue',
  'avenue',
  'av.',
  'boulevard',
  'bd',
  'chemin',
  'place',
  'allée',
  'allee',
  'impasse',
  'route',
  'quai',
  'cours',
  'square',
  'passage',
  'promenade',
  'faubourg',
  'sentier',
  'voie',
  'lieu-dit',
  'résidence',
  'residence',
  'cité',
  'cite',
  'hameau',
  'lotissement',
] as const
const LEADING_NUMBER_STREET_TYPE = new RegExp(
  `^(?:${LEADING_NUMBER_STREET_TYPES.map(escapeForPattern).join('|')})(?=\\s|$)`,
  'iu',
)
const LEADING_NUMBER_WITH_COMMA = /^\d[^,]*,/
const STARTS_WITH_DIGIT = /^\d/
const CONTAINS_WHITESPACE = /\s/
const ONLY_HOUSE_NUMBER_CHARACTERS = new RegExp(
  `^${HOUSE_NUMBER_CHARACTER}*$`,
  'u',
)

const collapseWhitespace = (value: string) => value.trim().replace(/\s+/g, ' ')

const findNoHouseNumberToken = (value: string) =>
  NO_HOUSE_NUMBER_TOKENS.find(
    (token) => token.toLowerCase() === value.toLowerCase(),
  )

export const normalizeHouseNumber = (value: string) => {
  const houseNumber = collapseWhitespace(value)
  return (
    findNoHouseNumberToken(houseNumber) ??
    houseNumber
      .replace(/\s*([-/])\s*/g, '$1')
      .replace(/(\d)\s+(?=\p{L})/gu, '$1')
      .replace(/(\p{L})\s+(?=\d)/gu, '$1')
  )
}

export const isHouseNumberValid = (value: string) => {
  const houseNumber = normalizeHouseNumber(value)
  return (
    houseNumber.length <= HOUSE_NUMBER_MAX_LENGTH &&
    HOUSE_NUMBER.test(houseNumber)
  )
}

const parseStreet = (
  pattern: RegExp,
  street: string,
): StreetParts | undefined => {
  const groups = pattern.exec(street)?.groups
  if (!groups || !isHouseNumberValid(groups.houseNumber)) {
    return undefined
  }
  return {
    street: groups.street,
    houseNumber: normalizeHouseNumber(groups.houseNumber),
  }
}

const isLeadingNumberAddress = (street: string, parts: StreetParts) =>
  LEADING_NUMBER_WITH_COMMA.test(street) ||
  LEADING_NUMBER_STREET_TYPE.test(parts.street)

const parseLeadingNumberStreet = (street: string) => {
  const parts = parseStreet(LEADING_HOUSE_NUMBER, street)
  return parts && isLeadingNumberAddress(street, parts) ? parts : undefined
}

export const splitStreet = (raw: string): StreetParts => {
  const street = collapseWhitespace(raw)
  return (
    parseStreet(TRAILING_HOUSE_NUMBER, street) ??
    parseLeadingNumberStreet(street) ?? { street, houseNumber: '' }
  )
}

export const composeStreet = ({
  street,
  houseNumber = '',
}: {
  street: string
  houseNumber?: string
}) =>
  [collapseWhitespace(street), normalizeHouseNumber(houseNumber)]
    .filter(Boolean)
    .join(' ')

export const streetRepeatsHouseNumber = ({
  street,
  houseNumber,
}: StreetParts) => {
  const trailingNumber = splitStreet(street).houseNumber.toLowerCase()
  return (
    trailingNumber !== '' &&
    trailingNumber === normalizeHouseNumber(houseNumber).toLowerCase()
  )
}

const isSameText = (a = '', b = '') =>
  collapseWhitespace(a) === collapseWhitespace(b)

export const isLocationChanged = (
  saved: AddressLocation,
  current: AddressLocation,
) =>
  composeStreet(saved) !== composeStreet(current) ||
  !isSameText(saved.zipcode, current.zipcode) ||
  !isSameText(saved.city, current.city) ||
  !isSameText(saved.countryId, current.countryId)

const describeInvalidHouseNumber = (
  value: string,
  messages: StreetAndNumberMessages,
) => {
  const houseNumber = normalizeHouseNumber(value)
  if (!STARTS_WITH_DIGIT.test(houseNumber)) {
    return messages.houseNumberMustStartWithDigit
  }
  if (CONTAINS_WHITESPACE.test(houseNumber)) {
    return messages.houseNumberHasSpaces
  }
  if (!ONLY_HOUSE_NUMBER_CHARACTERS.test(houseNumber)) {
    return messages.houseNumberHasInvalidCharacters
  }
  return messages.houseNumberTooLong
}

const checkHouseNumber = (
  houseNumber: string,
  messages: StreetAndNumberMessages,
) => {
  if (!houseNumber.trim()) {
    return messages.required
  }
  return isHouseNumberValid(houseNumber)
    ? undefined
    : describeInvalidHouseNumber(houseNumber, messages)
}

const checkStreet = (
  address: StreetParts,
  messages: StreetAndNumberMessages,
) => {
  if (streetRepeatsHouseNumber(address)) {
    return messages.repeatedHouseNumber
  }
  return composeStreet(address).length > STREET_LINE_MAX_LENGTH
    ? messages.streetLineTooLong
    : undefined
}

export const getStreetAndNumberIssues = (
  current: AddressLocation,
  saved?: AddressLocation,
  messages: StreetAndNumberMessages = STREET_AND_NUMBER_MESSAGES,
): StreetAndNumberIssue[] => {
  if (saved && !isLocationChanged(saved, current)) {
    return []
  }
  const houseNumberMessage = checkHouseNumber(current.houseNumber, messages)
  if (houseNumberMessage) {
    return [{ field: 'houseNumber', message: houseNumberMessage }]
  }
  const streetMessage = checkStreet(current, messages)
  return streetMessage ? [{ field: 'street', message: streetMessage }] : []
}
